import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Truck, User, Home, Phone, Mail, X, AlertCircle } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react"; // Import Auth0 hook
import AuthenticationModal from "./AuthenticationModal";
import EmptyCartNotice from "./EmptyCartNotice";
import StatusMessage from "./StatusMessage";
import BillingForm from "./BillingForm";
import OrderSummary from "./OrderSummary";
import SignUp from "./SignUp"; // Import SignUp component for unauthenticated users

// Constants
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_API = process.env.REACT_APP_AUTH0_API;

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      {children}
    </div>
  );
}

// Profile Completion Modal Component
function ProfileCompletionModal({ isOpen, onClose, onCompleteProfile }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>
        
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Complete Your Profile
          </h3>
          
          <p className="text-sm text-gray-500 mb-6">
            To proceed with checkout, please complete your profile information first. 
            This helps us process your order more efficiently.
          </p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onCompleteProfile}
              className="px-4 py-2 text-sm font-medium text-white bg-[#D4AF37] border border-transparent rounded-md hover:bg-[#B8941F] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37] transition-colors"
            >
              Complete Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage() {
  // Get initial cart items from location state (passed from CartPage)
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems: initialCartItems = [] } = location.state || {};

  // Use Auth0 hooks instead of manual token handling
  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    loginWithRedirect, 
    getAccessTokenSilently 
  } = useAuth0();

  // State for user profile and cart items
  const [userProfile, setUserProfile] = useState(null);
  const [cartItems, setCartItems] = useState(initialCartItems);
  
  // Add states for order submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);  
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Add state for discount
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  // Add state for profile completion modal
  const [showProfileModal, setShowProfileModal] = useState(false);

  // State for billing and shipping form
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    sameAsBilling: true,
    shippingAddress: "",
    shippingCity: "",
    shippingState: "",
    shippingZip: "",
    paymentMethod: "card", // Default payment method
  });

  // Effect to check authentication status and fetch cart items
  useEffect(() => {
    // Only fetch profile when Auth0 authentication is completed
    if (!auth0Loading) {
      if (isAuthenticated && auth0User) {
        // User is authenticated, fetch profile and cart
        fetchUserProfile(auth0User.email);
        fetchCartItems(auth0User.email);
      } else {
        // Not authenticated, just set loading to false
        setLoading(false);
      }
    }
  }, [auth0Loading, isAuthenticated, auth0User]);

  // Fetch user profile data from API using Auth0 token
  const fetchUserProfile = async (email) => {
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const encodedEmail = encodeURIComponent(email);
      const response = await fetch(`${API_BASE_URL}/get-user-profile/${encodedEmail}/`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      
      if (response.ok) {
        const profileData = await response.json();
        setUserProfile(prev => ({ ...prev, ...profileData }));
        
        setFormData(prevData => ({
          ...prevData,
          fullName: profileData.name || `${profileData.first_name} ${profileData.last_name}`,
          email: profileData.email || email,
          phone: profileData.phone || "",
          address: profileData.address?.street || "",
          city: profileData.address?.city || "",
          state: profileData.address?.state || "",
          zip: profileData.address?.zip || "",
        }));
      } else if (response.status === 404) {
        // Handle user not found case
        const errorData = await response.json();
        if (errorData.error === "User not found") {
          console.log("User profile not found, showing completion modal");
          setShowProfileModal(true);
        }
      } else {
        console.error("Failed to fetch user profile");
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cart items from GetCartView using Auth0 token
  const fetchCartItems = async (email) => {
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API // Make sure this matches your backend API_IDENTIFIER
        }
      });
      
      const encodedEmail = encodeURIComponent(email);
      const response = await fetch(`${API_BASE_URL}/cart/${encodedEmail}/`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      
      if (response.ok) {
        const cartData = await response.json();
        setCartItems(cartData || []);
        
      } else {
        console.error("Failed to fetch cart items");
        setCartItems([]);
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setCartItems([]);
    }
  };

  // Helper function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // Redirect to Auth0 login
  const handleRedirectToAuth = () => {
    loginWithRedirect({
      appState: { returnTo: location.pathname }
    });
  };

  // Handle profile completion modal actions
  const handleCompleteProfile = () => {
    // Store current cart items and billing page state for return
    sessionStorage.setItem('billingPageState', JSON.stringify({
      cartItems,
      formData,
      appliedDiscount,
      returnPath: location.pathname
    }));
    
    // Navigate to profile page
    navigate('/profile');
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    // Optionally redirect to shop or previous page
    navigate('/shop');
  };

  // Check if cart has items
  const hasCartItems = cartItems && cartItems.length > 0;
  
  // Calculate shipping cost based on state
  const calculateShippingCost = () => {
    const relevantState = formData.sameAsBilling ? formData.state : formData.shippingState;
    return relevantState && relevantState.toLowerCase() === "kerala" ? 120 : 180;
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.total_price, 0);
  const shippingCost = hasCartItems ? calculateShippingCost() : 0;
  const total = subtotal + shippingCost;
  const finalTotal = total - appliedDiscount;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    if (formError) {
      setFormError(null);
    }
  };

  
  const handleDiscountUpdate = (discountAmount) => {
    setAppliedDiscount(discountAmount);
  };

  
  const initiatePayment = async (orderData) => {
    try {
      setIsProcessingPayment(true);
      
      // Create redirect URL for payment callback
      const currentDomain = window.location.origin;
      const orderParams = new URLSearchParams({
        orderId: orderData.order_id,
        total: finalTotal.toString(),
        status: 'success' 
      }).toString();
      
      const redirectUrl = `${currentDomain}/payment-callback?${orderParams}`;
      
      const paymentData = {
        amount: finalTotal,
        redirect_url: redirectUrl
      };


      const response = await fetch(`${API_BASE_URL}/api/phonepe/create-payment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
      });

      if (response.ok) {
        const paymentResponse = await response.json();
        
        // Check if payment initiation was successful
        if (paymentResponse.success && paymentResponse.checkout_url) {
          // Store order and payment information for callback handling
          sessionStorage.setItem('pendingOrder', JSON.stringify({
            orderData,
            billingInfo: formData,
            appliedDiscount,
            finalTotal,
            merchantOrderId: paymentResponse.merchant_order_id,
            paymentAmount: paymentResponse.amount
          }));
          
          
          // Redirect to payment gateway
          window.location.href = paymentResponse.checkout_url;
        } else {
          throw new Error(paymentResponse.error || "Payment initiation failed - no checkout URL received");
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      setFormError(`Payment initiation failed: ${error.message}`);
      setIsProcessingPayment(false);
      scrollToTop();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      handleRedirectToAuth();
      return;
    }
    
    // Check if profile is complete before allowing checkout
    if (showProfileModal || !userProfile) {
      setShowProfileModal(true);
      return;
    }
    
    if (!hasCartItems) {
      alert("Your cart is empty. Please add items before proceeding to checkout.");
      navigate("/shop");
      return;
    }
    
    // Add loading state
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API // Make sure this matches your backend API_IDENTIFIER
        }
      });
      
      // Format the order data according to the API requirements
      const orderData = {
        user_id: auth0User.email,
        cart_items: cartItems.map(item => ({
          cart_id: item.cart_id,
          product_id: item.product_id,
          name: item.name,
          size: item.size,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          image: item.image
        })),
        billing_info: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          paymentMethod: formData.paymentMethod
        },
        shipping_info: formData.sameAsBilling
          ? {
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zip: formData.zip,
            }
          : {
              address: formData.shippingAddress,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingZip,
            },
        order_summary: {
          subtotal,
          shippingCost,
          discount: appliedDiscount,
          total,
          finalTotal,
        },
        status: "pending",
      };


      const response = await fetch(`${API_BASE_URL}/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      const responseData = await response.json();
      
      if (response.ok) {
        setOrderSuccess(true);
        
        // After successful order creation, initiate payment
        await initiatePayment(responseData);
        
      } else {
        // Handle specific error codes from API
        if (response.status === 400) {
          setFormError(responseData.error || "Please check your form details and try again.");
        } else if (response.status === 404) {
          setFormError(responseData.error || "User or cart items not found.");
        } else {
          setFormError(responseData.error || "An error occurred while processing your order.");
        }
        setOrderSuccess(false);
        scrollToTop(); 
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      setFormError("Network error. Please check your connection and try again.");
      setOrderSuccess(false);
      scrollToTop();
    } finally {
      setIsSubmitting(false);
    }
  };

  // If Auth0 is still loading, show loading state
  if (auth0Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Checking authentication...</p>
      </div>
    );
  }

  // If user is not authenticated, show SignUp component
  if (!isAuthenticated) {
    return (
      <AuthLayout>
        <SignUp redirectPath={location.pathname} />
      </AuthLayout>
    );
  }

  // If profile is still loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Loading Profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-1 px-4 md:px-8 mt-12">
      <div className="max-w-6xl mx-auto">
        <h1
          className="text-3xl md:text-4xl text-[#333333] text-center mb-8 relative"
          style={{ fontFamily: "serif" }}
        >
          Billing & Shipping
          <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
        </h1>

        <EmptyCartNotice hasCartItems={hasCartItems} navigate={navigate} />
        <StatusMessage formError={formError} />

        <div className="flex flex-col lg:flex-row gap-8">
          <BillingForm 
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting || isProcessingPayment}
            hasCartItems={hasCartItems}
            finalTotal={finalTotal}
            isProcessingPayment={isProcessingPayment}
          />

          <OrderSummary 
            cartItems={cartItems}
            hasCartItems={hasCartItems}
            subtotal={subtotal}
            shippingCost={shippingCost}
            total={total}
            onDiscountUpdate={handleDiscountUpdate}
          />
        </div>
      </div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={handleCloseProfileModal}
        onCompleteProfile={handleCompleteProfile}
      />
    </div>
  );
}